import { useRef, useState } from 'react';
import { Download, Loader2, Paperclip, Trash2, Upload } from 'lucide-react';
import { toast } from 'sonner';
import {
  useTaskAttachments,
  useUploadTaskAttachment,
  useDeleteTaskAttachment,
} from '../../hooks/queries/useTaskCollaboration';
import { getAttachmentSignedUrl } from '../../services/tasks/taskCollaborationService';
import { useAuthStore } from '../../store/useAuthStore';

import { formatAppDate } from '../../lib/dateFormat';
interface TaskAttachmentsSectionProps {
  taskId: string;
}

const MAX_SIZE_BYTES = 25 * 1024 * 1024; // 25 MB

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} o`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} Ko`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} Mo`;
}

export function TaskAttachmentsSection({ taskId }: TaskAttachmentsSectionProps) {
  const { data: attachments = [], isLoading } = useTaskAttachments(taskId);
  const uploadMut = useUploadTaskAttachment();
  const deleteMut = useDeleteTaskAttachment(taskId);
  const me = useAuthStore((s) => s.collaborator);
  const isAdmin = me?.role === 'admin' || me?.role === 'secretary';

  const inputRef = useRef<HTMLInputElement>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  function pickFile() {
    inputRef.current?.click();
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // reset
    if (!file) return;
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(`Fichier trop volumineux (max ${formatBytes(MAX_SIZE_BYTES)}).`);
      return;
    }
    uploadMut.mutate(
      { taskId, file },
      {
        onError: (err) => toast.error(`Upload impossible : ${err.message}`),
        onSuccess: () => toast.success(`${file.name} ajouté.`),
      },
    );
  }

  async function handleDownload(attachment: { id: string; storage_path: string; filename: string }) {
    try {
      setDownloadingId(attachment.id);
      const url = await getAttachmentSignedUrl(attachment.storage_path, 60);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(`Téléchargement impossible : ${(err as Error).message}`);
    } finally {
      setDownloadingId(null);
    }
  }

  function handleDelete(attachmentId: string, name: string) {
    if (!window.confirm(`Supprimer la pièce jointe « ${name} » ?`)) return;
    deleteMut.mutate(attachmentId, {
      onError: (err) => toast.error(`Suppression impossible : ${err.message}`),
    });
  }

  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold text-cime-text-muted uppercase tracking-wide">
          Pièces jointes
          {attachments.length > 0 && (
            <span className="ml-1.5 font-normal">({attachments.length})</span>
          )}
        </h3>
        <button
          type="button"
          onClick={pickFile}
          disabled={uploadMut.isPending}
          className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-neutral-200 text-xs text-cime-text-muted hover:bg-neutral-50 hover:border-cime-primary/30 disabled:opacity-50"
        >
          {uploadMut.isPending ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : (
            <Upload className="w-3.5 h-3.5" />
          )}
          Ajouter
        </button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleFileChange}
        />
      </div>

      {isLoading && (
        <div className="text-xs text-cime-text-muted flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Chargement…
        </div>
      )}
      {!isLoading && attachments.length === 0 && (
        <p className="text-xs text-cime-text-muted italic">
          Aucune pièce jointe.
        </p>
      )}

      <ul className="space-y-1">
        {attachments.map((a) => {
          const canDelete = a.uploaded_by === me?.id || isAdmin;
          return (
            <li
              key={a.id}
              className="group flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-neutral-50"
            >
              <Paperclip className="w-4 h-4 text-cime-text-muted flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-cime-text-primary truncate">
                  {a.filename}
                </div>
                <div className="text-[11px] text-cime-text-muted">
                  {formatBytes(a.size_bytes)} •{' '}
                  {formatAppDate(a.created_at)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => handleDownload(a)}
                disabled={downloadingId === a.id}
                className="text-neutral-400 hover:text-cime-primary p-1"
                aria-label="Télécharger"
              >
                {downloadingId === a.id ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Download className="w-3.5 h-3.5" />
                )}
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={() => handleDelete(a.id, a.filename)}
                  className="opacity-0 group-hover:opacity-100 transition text-neutral-400 hover:text-red-600 p-1"
                  aria-label="Supprimer"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
